import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { COLORS, MUSEUM_MODELS, createMuseumArchitecture, loadCustomMuseum, exportSceneToJSON, exportSceneToGLB } from './MuseumUtils';
import { createPalaceMuseum } from './palace/PalaceMuseum';
import { MuseumLayoutManager } from './topologyUtils';
import PalaceSaveModal from './ui/PalaceSaveModal';
import './GenerativeMuseum.css';

// Mobile device detection utility
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || // iPad detection
         window.matchMedia('(max-width: 768px)').matches;
};

// Touch helper utilities
const getTouchPos = (touch, element) => {
  const rect = element.getBoundingClientRect();
  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top
  };
};

const getDistance = (touch1, touch2) => {
  return Math.sqrt(
    Math.pow(touch2.clientX - touch1.clientX, 2) +
    Math.pow(touch2.clientY - touch1.clientY, 2)
  );
};

// OrbitControls (three@0.174.0) only exposes getAzimuthalAngle()/getPolarAngle()
// as read-only getters — there is no azimuthalAngle/polarAngle setter. Orbiting
// has to be done by hand: recompute the camera's spherical offset from the
// target, adjust it, and write the position back.
const rotateOrbitControls = (controls, deltaAzimuth, deltaPolar) => {
  const offset = new THREE.Vector3().subVectors(controls.object.position, controls.target);
  const spherical = new THREE.Spherical().setFromVector3(offset);

  spherical.theta -= deltaAzimuth;
  spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi + deltaPolar));
  spherical.makeSafe();

  offset.setFromSpherical(spherical);
  controls.object.position.copy(controls.target).add(offset);
  controls.update();
};

// OrbitControls' dollyIn()/dollyOut() are private (_dollyIn/_dollyOut) in
// three@0.174.0 and not part of the public API. Dolly by hand instead: scale
// the camera's distance to the target directly, respecting min/maxDistance.
const dollyOrbitControls = (controls, dollyScale, direction) => {
  const offset = new THREE.Vector3().subVectors(controls.object.position, controls.target);
  const distance = offset.length();
  const nextDistance = direction === 'in' ? distance / dollyScale : distance * dollyScale;
  const clampedDistance = Math.max(controls.minDistance, Math.min(controls.maxDistance, nextDistance));

  offset.setLength(clampedDistance);
  controls.object.position.copy(controls.target).add(offset);
  controls.update();
};

// Looking around here is an orbit, not a head turn: the target sits a metre in
// front of the visitor and OrbitControls swings the *camera* around it, so a
// drag translates the body by up to two metres as well as turning the view.
// That is how you end up outside a wall you can't walk through. Settle the
// camera against the museum's colliders once a frame — after the controls have
// had their say, whoever moved them — and carry the target along by the same
// vector so the pushout never yaws the view.
const _viewOffset = new THREE.Vector3();

const settleAgainstWalls = (controls, collide) => {
  if (!collide) return;
  _viewOffset.subVectors(controls.target, controls.object.position);
  collide(controls.object.position);
  controls.target.copy(controls.object.position).add(_viewOffset);
};

// Virtual Joystick Component
const VirtualJoystick = ({ onMove, onEnd, disabled }) => {
  const joystickRef = useRef(null);
  const knobRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  const handleStart = (clientX, clientY) => {
    if (disabled) return;
    setIsDragging(true);
  };
  
  const handleMove = (clientX, clientY) => {
    if (!isDragging || disabled || !joystickRef.current) return;
    
    const rect = joystickRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const maxDistance = centerX - 10; // Account for knob size
    
    let deltaX = clientX - rect.left - centerX;
    let deltaY = clientY - rect.top - centerY;
    
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (distance > maxDistance) {
      deltaX = (deltaX / distance) * maxDistance;
      deltaY = (deltaY / distance) * maxDistance;
    }
    
    setPosition({ x: deltaX, y: deltaY });
    
    // Convert to movement values (-1 to 1)
    const moveX = deltaX / maxDistance;
    const moveY = -deltaY / maxDistance; // Invert Y for forward/backward
    
    onMove({ x: moveX, y: moveY });
  };
  
  const handleEnd = () => {
    setIsDragging(false);
    setPosition({ x: 0, y: 0 });
    onEnd();
  };
  
  return (
    <div 
      ref={joystickRef}
      className={`virtual-joystick ${disabled ? 'disabled' : ''}`}
      onTouchStart={(e) => {
        e.preventDefault();
        const touch = e.touches[0];
        handleStart(touch.clientX, touch.clientY);
      }}
      onTouchMove={(e) => {
        e.preventDefault();
        const touch = e.touches[0];
        handleMove(touch.clientX, touch.clientY);
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        handleEnd();
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        handleStart(e.clientX, e.clientY);
      }}
      onMouseMove={(e) => {
        if (isDragging) {
          e.preventDefault();
          handleMove(e.clientX, e.clientY);
        }
      }}
      onMouseUp={(e) => {
        e.preventDefault();
        handleEnd();
      }}
      onMouseLeave={handleEnd}
    >
      <div 
        ref={knobRef}
        className="joystick-knob"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`
        }}
      />
    </div>
  );
};

// Mobile Control Buttons Component
const MobileControls = ({ 
  onJoystickMove, 
  onJoystickEnd, 
  onVerticalMove, 
  onSprintToggle,
  disabled 
}) => {
  return (
    <div className={`mobile-controls ${disabled ? 'disabled' : ''}`}>
      {/* Virtual Joystick */}
      <VirtualJoystick 
        onMove={onJoystickMove}
        onEnd={onJoystickEnd}
        disabled={disabled}
      />
      
      {/* Vertical Movement Buttons */}
      <div className="vertical-controls">
        <button 
          className="vertical-button up-button"
          onTouchStart={(e) => { e.preventDefault(); onVerticalMove('up', true); }}
          onTouchEnd={(e) => { e.preventDefault(); onVerticalMove('up', false); }}
          onMouseDown={(e) => { e.preventDefault(); onVerticalMove('up', true); }}
          onMouseUp={(e) => { e.preventDefault(); onVerticalMove('up', false); }}
          onMouseLeave={(e) => { e.preventDefault(); onVerticalMove('up', false); }}
          disabled={disabled}
        >
          ↑
        </button>
        <button 
          className="vertical-button down-button"
          onTouchStart={(e) => { e.preventDefault(); onVerticalMove('down', true); }}
          onTouchEnd={(e) => { e.preventDefault(); onVerticalMove('down', false); }}
          onMouseDown={(e) => { e.preventDefault(); onVerticalMove('down', true); }}
          onMouseUp={(e) => { e.preventDefault(); onVerticalMove('down', false); }}
          onMouseLeave={(e) => { e.preventDefault(); onVerticalMove('down', false); }}
          disabled={disabled}
        >
          ↓
        </button>
      </div>
      
      {/* Sprint Toggle Button */}
      <button 
        className="sprint-button"
        onTouchStart={(e) => { e.preventDefault(); onSprintToggle(true); }}
        onTouchEnd={(e) => { e.preventDefault(); onSprintToggle(false); }}
        onMouseDown={(e) => { e.preventDefault(); onSprintToggle(true); }}
        onMouseUp={(e) => { e.preventDefault(); onSprintToggle(false); }}
        onMouseLeave={(e) => { e.preventDefault(); onSprintToggle(false); }}
        disabled={disabled}
      >
        🏃
      </button>
    </div>
  );
};

const GenerativeMuseum = ({ tweets }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationFrameRef = useRef(null);
  const centralpieceRef = useRef(null);
  const skyboxRef = useRef(null);
  const environmentRef = useRef(null);
  // Holds the cleanup closure built inside init() once the deferred scene
  // setup actually runs, so the effect's own cleanup can invoke it.
  const initCleanupRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  // What the palace generator is doing right now — it runs for seconds on a
  // full archive, so the loading overlay says where it has got to.
  const [loadingStatus, setLoadingStatus] = useState('');
  const [logMessages, setLogMessages] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [clusterSelectionOpen, setClusterSelectionOpen] = useState(true);
  const [availableClusters, setAvailableClusters] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [filteredTweets, setFilteredTweets] = useState(tweets);
  const [useCustomModel, setUseCustomModel] = useState(true);
  const [selectedModel, setSelectedModel] = useState('gallery'); // 'standard' or 'gallery'
  const [isMobile, setIsMobile] = useState(false);
  const [advancedGestures, setAdvancedGestures] = useState(false);
  const [touchControls, setTouchControls] = useState({
    joystick: { active: false, startPos: null, currentPos: null },
    camera: { lastTouch: null, initialDistance: null, rotating: false },
    vertical: { movingUp: false, movingDown: false }
  });
  // Get initial camera position based on selected model
  const getInitialCameraPosition = () => {
    const modelConfig = MUSEUM_MODELS[selectedModel];
    return {
      position: modelConfig.cameraPosition,
      target: { x: 0, y: modelConfig.cameraPosition.y, z: 0 },
      movementSpeed: 0.5,
      keysPressed: {}
    };
  };
  
  const [cameraState, setCameraState] = useState(getInitialCameraPosition());
  const [cameraPosition, setCameraPosition] = useState({ x: 0, y: 0, z: 0 });
  
  const cameraStateRef = useRef(cameraState);

  // Keep the ref updated with the latest state
  useEffect(() => {
    cameraStateRef.current = cameraState;
  }, [cameraState]);

  // The save whose frame is currently open, from clicking a painting in the
  // palace. The ref is what the scene's own listeners read: they are installed
  // once by init() and would otherwise close over the state at mount.
  const [openSave, setOpenSave] = useState(null);
  const openSaveRef = useRef(null);
  useEffect(() => {
    openSaveRef.current = openSave;
  }, [openSave]);

  // Hand the building back to the visitor when the frame closes: the orbit
  // controls and the walk keys are both dead while a card is open, or typing
  // "wasd" into an edit field walks you through a wall.
  useEffect(() => {
    if (controlsRef.current) controlsRef.current.enabled = !openSave;
    if (!openSave) return;
    // Whatever was held down when the frame opened is not still held now.
    setCameraState(prev => ({ ...prev, keysPressed: {} }));
  }, [openSave]);

  // Detect mobile device on mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);
  
  // Gyroscope/Device Orientation Support
  useEffect(() => {
    if (!isMobile || !advancedGestures) return;
    
    let lastOrientation = { alpha: 0, beta: 0, gamma: 0 };
    
    const handleDeviceOrientation = (event) => {
      if (!controlsRef.current || clusterSelectionOpen) return;
      
      const { alpha, beta, gamma } = event;
      
      // Only update if we have meaningful orientation data
      if (alpha !== null && beta !== null && gamma !== null) {
        // Calculate the change in orientation
        const deltaAlpha = (alpha - lastOrientation.alpha) * (Math.PI / 180);
        const deltaBeta = (beta - lastOrientation.beta) * (Math.PI / 180);
        
        // Apply sensitivity scaling
        const sensitivity = 0.1;
        
        // Update camera rotation based on device orientation
        const controls = controlsRef.current;

        // Horizontal rotation (left/right) and vertical rotation (up/down,
        // clamped) applied together via the shared orbit helper.
        rotateOrbitControls(controls, deltaAlpha * sensitivity, deltaBeta * sensitivity);

        lastOrientation = { alpha, beta, gamma };
      }
    };
    
    // Request permission for iOS 13+
    const requestPermission = async () => {
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        try {
          const permission = await DeviceOrientationEvent.requestPermission();
          if (permission === 'granted') {
            window.addEventListener('deviceorientation', handleDeviceOrientation);
            log('Gyroscope enabled for camera control');
          } else {
            log('Gyroscope permission denied');
          }
        } catch (error) {
          console.error('Error requesting device orientation permission:', error);
          log('Gyroscope not available');
        }
      } else {
        // Non-iOS devices or older iOS versions
        window.addEventListener('deviceorientation', handleDeviceOrientation);
        log('Gyroscope enabled for camera control');
      }
    };
    
    requestPermission();
    
    // Cleanup
    return () => {
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
    };
  }, [isMobile, advancedGestures, clusterSelectionOpen]);

  // Logging function with unique ID generator
  const log = (message) => {
    console.log(`Museum: ${message}`);
    setLogMessages(prev => [...prev, { id: Date.now() + Math.random(), message }]);
  };
  
  // Create a beautiful sky background inspired by the WebGPU cloud example
  const createSkyBackground = (scene) => {
    log(`Creating ${isMobile ? 'mobile-optimized' : 'full-quality'} sky with clouds`);
    
    // Create a Sky mesh from three's sky implementation
    const sky = new Sky();
    sky.scale.setScalar(450000);
    scene.add(sky);
    
    // Get sky uniform references
    const skyUniforms = sky.material.uniforms;
    
    // Set sky parameters for a beautiful look
    skyUniforms['turbidity'].value = 10;
    skyUniforms['rayleigh'].value = 2;
    skyUniforms['mieCoefficient'].value = 0.005;
    skyUniforms['mieDirectionalG'].value = 0.8;
    
    // Define sun position parameters for dramatic lighting angle
    const sunPosition = new THREE.Vector3();
    const theta = Math.PI * (0.25); // Lower sun altitude for longer shadows
    const phi = 2 * Math.PI * (0.35); // Sun azimuth for angled light
    
    // Calculate sun position from angles
    sunPosition.x = Math.cos(phi);
    sunPosition.y = Math.sin(phi) * Math.sin(theta);
    sunPosition.z = Math.sin(phi) * Math.cos(theta);
    
    skyUniforms['sunPosition'].value.copy(sunPosition);
    
    // Create a sun directional light with moderate intensity for softer shadows
    const sunLight = new THREE.DirectionalLight(0xffffeF, 1.8);
    sunLight.position.set(sunPosition.x * 100, sunPosition.y * 100, sunPosition.z * 100);
    sunLight.castShadow = true;
    scene.add(sunLight);
    
    // Improve shadow quality and make shadows sharper
    sunLight.shadow.mapSize.width = 4096; // Higher resolution shadows
    sunLight.shadow.mapSize.height = 4096;
    sunLight.shadow.camera.near = 0.5;
    sunLight.shadow.camera.far = 1000;
    sunLight.shadow.bias = -0.0005; // Slightly increased to reduce shadow acne while keeping shadows soft
    
    // Set up shadow camera to cover more area
    const shadowSize = 200;
    sunLight.shadow.camera.left = -shadowSize;
    sunLight.shadow.camera.right = shadowSize;
    sunLight.shadow.camera.top = shadowSize;
    sunLight.shadow.camera.bottom = -shadowSize;
    
    // Add blur for softer shadows
    sunLight.shadow.radius = 3; // Higher values = softer, more blurred shadows
    
    // Debug helper to visualize the shadow camera frustum
    // const helper = new THREE.CameraHelper(sunLight.shadow.camera);
    // scene.add(helper);
    
    // Increase ambient light intensity for softer shadows and less contrast
    const ambientLight = new THREE.AmbientLight(0xC9E6FF, 0.65);
    scene.add(ambientLight);
    
    // Add some clouds using geometry to simulate volumetric effect
    // Create 3D cloud particles
    const createClouds = () => {
      // Group to hold all clouds
      const cloudsGroup = new THREE.Group();
      
      // Create cloud material with soft gradients
      const cloudMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xaaaaaa,
        emissiveIntensity: 0.2,
        transparent: true,
        opacity: 0.85,
        roughness: 1.0,
        metalness: 0.0
      });
      
      // Create cloud clusters (fewer on mobile for performance)
      const cloudCount = isMobile ? 8 : 20;
      for (let i = 0; i < cloudCount; i++) {
        const cloudCluster = new THREE.Group();
        
        // Random position high in the sky
        const radius = 250 + Math.random() * 200;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI * 0.3; // Keep clouds in upper hemisphere
        
        // Convert to Cartesian coordinates
        cloudCluster.position.x = radius * Math.sin(phi) * Math.cos(theta);
        cloudCluster.position.y = 150 + Math.random() * 100; // Height above ground
        cloudCluster.position.z = radius * Math.sin(phi) * Math.sin(theta);
        
        // Randomly rotate the cloud
        cloudCluster.rotation.set(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        );
        
        // Create cloud puffs per cluster (fewer on mobile)
        const basePuffCount = isMobile ? 3 : 5;
        const maxPuffCount = isMobile ? 6 : 10;
        const puffCount = basePuffCount + Math.floor(Math.random() * maxPuffCount);
        
        for (let j = 0; j < puffCount; j++) {
          // Create cloud puff with stretched sphere
          const puffSize = 10 + Math.random() * 25;
          const puffGeometry = new THREE.SphereGeometry(puffSize, 8, 8);
          
          // Create mesh and position randomly within cluster
          const puff = new THREE.Mesh(puffGeometry, cloudMaterial);
          puff.castShadow = true; // Make each cloud puff cast a shadow
          
          // Position puff within cluster
          puff.position.set(
            (Math.random() - 0.5) * 50,
            (Math.random() - 0.5) * 15,
            (Math.random() - 0.5) * 50
          );
          
          // Scale puff to be wider than tall for cloud-like appearance
          const scaleY = 0.3 + Math.random() * 0.4;
          puff.scale.set(1, scaleY, 1);
          
          // Add puff to cluster
          cloudCluster.add(puff);
        }
        
        // Add animation data to cluster
        cloudCluster.userData.animation = {
          rotationSpeed: { 
            x: 0, 
            y: (0.0001 + Math.random() * 0.0002) * (Math.random() > 0.5 ? 1 : -1), 
            z: 0 
          },
          floatSpeed: 0.1 + Math.random() * 0.2,
          floatHeight: 2 + Math.random() * 5,
          startTime: Math.random() * 100
        };
        
        // Store original Y position for floating animation
        cloudCluster.userData.originalY = cloudCluster.position.y;
        
        // Add cluster to clouds group
        cloudsGroup.add(cloudCluster);
      }
      
      return cloudsGroup;
    };
    
    // Create and add clouds
    const clouds = createClouds();
    scene.add(clouds);
    
    // Create a more detailed ground plane with a grid pattern to enhance shadow visibility
    const groundSize = 10000;
    const groundGeometry = new THREE.PlaneGeometry(groundSize, groundSize, 32, 32);
    
    // Create a material that will emphasize shadows
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a3a48,
      roughness: 0.88,
      metalness: 0.05,
      side: THREE.FrontSide
    });
    
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2; // Rotate to be horizontal
    ground.position.y = -50; // Below the museum
    ground.receiveShadow = true;
    
    // Add a grid pattern to help visualize shadows
    const gridHelper = new THREE.GridHelper(500, 50, 0x555555, 0x222222);
    gridHelper.position.y = -49.9; // Slightly above ground to avoid z-fighting
    gridHelper.material.transparent = true;
    gridHelper.material.opacity = 0.3; // Semi-transparent grid
    scene.add(gridHelper);
    
    scene.add(ground);
    
    // Add moderate atmospheric fog for depth and softer shadows
    scene.fog = new THREE.FogExp2(0xaac5ea, 0.0012);
    
    log('Enhanced sky with clouds created');
    return sky;
  };
  
  // Load topic clusters data from the analysis summary JSON
  const fetchTopicData = useCallback(async () => {
    try {
      log('Fetching topic clusters data...');
      
      // Try to fetch the analysis_results.json file
      const response = await fetch('/exports/insights/analysis_results.json');
      
      if (!response.ok) {
        throw new Error('Failed to fetch analysis results');
      }
      
      const data = await response.json();
      
      // Check if semantic_clusters data exists in the summary
      if (data && data.semantic_clusters && data.semantic_clusters.clusters) {
        // Convert from object to array format
        const clusters = Object.entries(data.semantic_clusters.clusters)
          .filter(([id]) => id !== '-1') // Skip any "unknown" cluster with ID -1
          .map(([id, cluster]) => ({
            topic_id: id,
            topic_label: cluster.topic_label || `Topic ${Number(id) + 1}`,
            topic_terms: cluster.topic_terms || [],
            // Convert terms to keywords for compatibility
            keywords: cluster.topic_terms || [],
            size: cluster.size || 0,
            // Include any other relevant data
            original_label: cluster.original_label
          }));
        
        log(`Found ${clusters.length} topic clusters from analysis`);
        setAvailableClusters(clusters);
      } else {
        // If no semantic clusters found, use default categories
        throw new Error('No semantic clusters found in analysis results');
      }
    } catch (error) {
      console.error('Error loading topic data:', error);
      log('Using default topic categories');
      
      // Create default categories based on common tweet topics
      const defaultTopics = [
        { 
          topic_id: "0", 
          topic_label: "Technology", 
          keywords: ['software', 'code', 'programming', 'tools', 'tech'],
          topic_terms: ['software', 'code', 'programming', 'tools', 'tech']
        },
        { 
          topic_id: "1", 
          topic_label: "Social Media", 
          keywords: ['twitter', 'posts', 'content', 'social', 'media'],
          topic_terms: ['twitter', 'posts', 'content', 'social', 'media']
        },
        { 
          topic_id: "2", 
          topic_label: "Art & Design", 
          keywords: ['art', 'design', 'creative', 'visual', 'color'],
          topic_terms: ['art', 'design', 'creative', 'visual', 'color']
        },
        { 
          topic_id: "3", 
          topic_label: "Productivity", 
          keywords: ['workflow', 'productivity', 'time', 'tasks', 'focus'],
          topic_terms: ['workflow', 'productivity', 'time', 'tasks', 'focus']
        },
        { 
          topic_id: "4", 
          topic_label: "Science & Research", 
          keywords: ['research', 'data', 'science', 'studies', 'analysis'],
          topic_terms: ['research', 'data', 'science', 'studies', 'analysis']
        }
      ];
      
      log(`Using ${defaultTopics.length} default topic categories`);
      setAvailableClusters(defaultTopics);
    }
  }, []);
  
  // Filter tweets by selected topic cluster - optimized for speed
  const filterTweetsByTopic = useCallback((topicId) => {
    // Ceiling on how many saves a topic contributes. Nothing trims this to the
    // plane count later, despite what this comment used to claim -- any surplus
    // is simply unused, while a shortfall leaves planes hidden. So keep it well
    // clear of the gallery's plane count, which grows as frames get filled in.
    const TWEET_LIMIT = 400;
    console.log(`[DEBUG] filterTweetsByTopic: Using TWEET_LIMIT=${TWEET_LIMIT}`);
    
    // Filter out Reddit saves from museum (but keep them in main app)
    const nonRedditTweets = tweets.filter(tweet => tweet.source !== 'reddit');

    // Generated museums hang the whole archive; the topic choice picks the
    // spawn wing instead of trimming the tweet list.
    if (MUSEUM_MODELS[selectedModel]?.type === 'generated') {
      log(`Palace: hanging all ${nonRedditTweets.length} saves (topic selects spawn wing)`);
      setFilteredTweets(nonRedditTweets);
      return;
    }

    if (!topicId && topicId !== 0) {
      // If no topic selected, just use a sample for better performance
      const sampleTweets = nonRedditTweets.slice(0, TWEET_LIMIT);
      log(`Using ${sampleTweets.length} sample tweets (excluding Reddit)`);
      setFilteredTweets(sampleTweets);
      return;
    }
    
    try {
      if (availableClusters) {
        // Find the cluster by ID (topics could be in any order in the array)
        const selectedTopic = availableClusters.find(cluster => cluster.topic_id === topicId);
        
        if (!selectedTopic) {
          throw new Error(`Topic ID ${topicId} not found`);
        }
        
        // Use topic_terms if available, otherwise fall back to keywords
        const keywords = selectedTopic.topic_terms || selectedTopic.keywords;
        
        // Pre-process keywords for faster matching
        const processedKeywords = keywords.map(k => k.toLowerCase());
        
        // Search the whole library, not a sample of it. This used to step over
        // every 50th save and stop at 300 candidates, so a topic was matched
        // against ~2% of the collection and came back with far fewer tweets than
        // the gallery has planes -- which is what left half the frames blank.
        // A substring match over ten thousand strings costs nothing, and the
        // TWEET_LIMIT break below still stops early once there is plenty.
        const matchedTweets = [];

        for (let i = 0; i < nonRedditTweets.length; i++) {
          const tweet = nonRedditTweets[i];
          const tweetText = (tweet.text || tweet.full_text || '').toLowerCase();
          
          for (let j = 0; j < processedKeywords.length; j++) {
            if (tweetText.includes(processedKeywords[j])) {
              matchedTweets.push(tweet);
              break; // Found a match for this tweet, move to next tweet
            }
          }
          
          // Stop if we have enough matches
          if (matchedTweets.length >= TWEET_LIMIT) break;
        }
        
        if (matchedTweets.length > 0) {
          log(`Found ${matchedTweets.length} tweets for topic "${selectedTopic.topic_label}"`);
          setFilteredTweets(matchedTweets);
        } else {
          // If no matches, just use a sample based on topic ID for consistency
          const sampleSize = Math.min(nonRedditTweets.length, TWEET_LIMIT);
          const topicIdNum = parseInt(topicId, 10) || 0; // Convert string ID to number safely
          const startIndex = (topicIdNum * 17) % Math.max(1, nonRedditTweets.length - sampleSize);
          const randomSample = nonRedditTweets.slice(startIndex, startIndex + sampleSize);
          
          log(`No keyword matches found, using ${randomSample.length} sample tweets for topic "${selectedTopic.topic_label}"`);
          setFilteredTweets(randomSample);
        }
      } else {
        // Topic not found, use default sample
        const sampleTweets = nonRedditTweets.slice(0, TWEET_LIMIT);
        log(`No topic information found, using ${sampleTweets.length} sample tweets (excluding Reddit)`);
        setFilteredTweets(sampleTweets);
      }
    } catch (error) {
      console.error('Error in topic filtering:', error);
      
      // Fallback: use first few tweets
      const sampleTweets = nonRedditTweets.slice(0, TWEET_LIMIT);
      log(`Using ${sampleTweets.length} sample tweets due to error (excluding Reddit)`);
      setFilteredTweets(sampleTweets);
    }
  }, [tweets, availableClusters, selectedModel]);

  // Load topic data when component mounts
  useEffect(() => {
    fetchTopicData();
  }, [fetchTopicData]);
  
  // Remove the pre-counting useEffect - we'll count during actual loading
  
  // Add tweet-themed centerpiece to the scene
  const addTweetCenterpiece = (scene) => {
    log('Creating tweet centerpiece');
    
    // Create a marble pedestal for the artwork
    const pedestalGeometry = new THREE.CylinderGeometry(2, 2.5, 1.5, 32);
    const pedestalMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.2,
      metalness: 0.1,
    });
    
    const pedestal = new THREE.Mesh(pedestalGeometry, pedestalMaterial);
    pedestal.position.set(0, 0.75, 0); // Positioned at center, half height off ground
    pedestal.castShadow = true;
    pedestal.receiveShadow = true;
    
    // Create decorative element around the pedestal
    const pedestalRing = new THREE.TorusGeometry(2.2, 0.1, 16, 32);
    const goldMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      roughness: 0.1,
      metalness: 1.0,
    });
    
    const ring = new THREE.Mesh(pedestalRing, goldMaterial);
    ring.position.set(0, 0.05, 0);
    ring.rotation.x = Math.PI / 2;
    ring.castShadow = true;
    
    // Create the complex torus knot geometry
    const artworkGeometry = new THREE.TorusKnotGeometry(1, 0.3, 100, 16);
    const artworkMaterial = new THREE.MeshStandardMaterial({
      color: COLORS.accent,
      roughness: 0.2,
      metalness: 0.8,
      emissive: COLORS.accent,
      emissiveIntensity: 0.3
    });
    
    const artwork = new THREE.Mesh(artworkGeometry, artworkMaterial);
    artwork.position.set(0, 3.5, 0); // Positioned above the pedestal
    artwork.castShadow = true;
    artwork.receiveShadow = true;
    
    // Add point light inside the knot for glow effect
    const pointLight = new THREE.PointLight(COLORS.accent, 1.5, 10);
    pointLight.position.set(0, 0, 0);
    artwork.add(pointLight);
    
    // Create a group to hold both pedestal and artwork
    const centerpieceGroup = new THREE.Group();
    centerpieceGroup.add(pedestal);
    centerpieceGroup.add(ring);
    centerpieceGroup.add(artwork);
    
    // Store reference to the artwork for animation
    centralpieceRef.current = artwork;
    
    // Add to scene
    sceneRef.current.add(centerpieceGroup);
    
    log('Added centerpiece to museum');
    
    return centerpieceGroup;
  };
  
  // Mobile joystick movement handler
  const handleJoystickMove = useCallback((movement) => {
    setCameraState(prev => ({
      ...prev,
      keysPressed: {
        ...prev.keysPressed,
        w: movement.y > 0.3,
        s: movement.y < -0.3,
        a: movement.x < -0.3,
        d: movement.x > 0.3
      }
    }));
  }, []);
  
  const handleJoystickEnd = useCallback(() => {
    setCameraState(prev => ({
      ...prev,
      keysPressed: {
        ...prev.keysPressed,
        w: false,
        s: false,
        a: false,
        d: false
      }
    }));
  }, []);
  
  const handleVerticalMove = useCallback((direction, isActive) => {
    setCameraState(prev => ({
      ...prev,
      keysPressed: {
        ...prev.keysPressed,
        [direction === 'up' ? ' ' : 'c']: isActive
      }
    }));
  }, []);
  
  const handleSprintToggle = useCallback((isActive) => {
    setCameraState(prev => ({
      ...prev,
      keysPressed: {
        ...prev.keysPressed,
        shift: isActive
      }
    }));
  }, []);
  
  // Touch gesture handlers for camera control
  const handleTouchStart = useCallback((e) => {
    if (!isMobile || clusterSelectionOpen) return;
    
    const touches = e.touches;
    if (touches.length === 1) {
      // Single touch - camera rotation
      const touch = touches[0];
      setTouchControls(prev => ({
        ...prev,
        camera: {
          ...prev.camera,
          lastTouch: { x: touch.clientX, y: touch.clientY },
          rotating: true
        }
      }));
    } else if (touches.length === 2) {
      // Two finger pinch - zoom
      const distance = getDistance(touches[0], touches[1]);
      setTouchControls(prev => ({
        ...prev,
        camera: {
          ...prev.camera,
          initialDistance: distance
        }
      }));
    }
  }, [isMobile, clusterSelectionOpen]);
  
  const handleTouchMove = useCallback((e) => {
    if (!isMobile || clusterSelectionOpen || !controlsRef.current) return;
    e.preventDefault();
    
    const touches = e.touches;
    if (touches.length === 1 && touchControls.camera.rotating) {
      // Camera rotation
      const touch = touches[0];
      const lastTouch = touchControls.camera.lastTouch;
      
      if (lastTouch) {
        const deltaX = touch.clientX - lastTouch.x;
        const deltaY = touch.clientY - lastTouch.y;
        
        // Apply rotation to controls
        const controls = controlsRef.current;
        rotateOrbitControls(controls, deltaX * 0.005, deltaY * 0.005);

        setTouchControls(prev => ({
          ...prev,
          camera: {
            ...prev.camera,
            lastTouch: { x: touch.clientX, y: touch.clientY }
          }
        }));
      }
    } else if (touches.length === 2) {
      // Pinch zoom
      const currentDistance = getDistance(touches[0], touches[1]);
      const initialDistance = touchControls.camera.initialDistance;
      
      if (initialDistance) {
        const scale = currentDistance / initialDistance;
        const controls = controlsRef.current;
        const camera = cameraRef.current;
        
        if (scale > 1.05) {
          // Zoom in
          dollyOrbitControls(controls, 1.05, 'in');
          setTouchControls(prev => ({
            ...prev,
            camera: { ...prev.camera, initialDistance: currentDistance }
          }));
        } else if (scale < 0.95) {
          // Zoom out
          dollyOrbitControls(controls, 1.05, 'out');
          setTouchControls(prev => ({
            ...prev,
            camera: { ...prev.camera, initialDistance: currentDistance }
          }));
        }
      }
    }
  }, [isMobile, clusterSelectionOpen, touchControls.camera]);
  
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile) return;
    
    setTouchControls(prev => ({
      ...prev,
      camera: {
        lastTouch: null,
        initialDistance: null,
        rotating: false
      }
    }));
  }, [isMobile]);
  
  // Function to update camera movement
  const updateCameraMovement = () => {
    const camera = cameraRef.current;
    if (!camera || !controlsRef.current) return;
  };
  
  useEffect(() => {
    if (!containerRef.current || clusterSelectionOpen) return;
    
    // Function to update camera position based on keys pressed
    const updateCameraMovement = () => {
      const camera = cameraRef.current;
      if (!camera || !controlsRef.current) return;
      
      // Get current keys pressed state and calculate effective movement speed
      const { keysPressed } = cameraStateRef.current;
      const baseSpeed = 0.065; // ~4 m/s at 60fps: a museum stroll, not a sprint
      const sprintMultiplier = keysPressed.shift ? 2.5 : 1; // Shift key for sprint
      const effectiveSpeed = baseSpeed * sprintMultiplier;
      
      // Get camera direction
      const direction = new THREE.Vector3();
      camera.getWorldDirection(direction);
      
      // Create a horizontal direction vector (ignore y component for flat movement)
      const horizontalDirection = new THREE.Vector3(direction.x, 0, direction.z).normalize();
      
      // Create right vector (perpendicular to direction)
      const rightVector = new THREE.Vector3()
        .crossVectors(new THREE.Vector3(0, 1, 0), horizontalDirection)
        .normalize();
      
      // Calculate movement vector based on keys pressed
      const movementVector = new THREE.Vector3(0, 0, 0);
      
      // Skip if no movement keys are pressed
      if (!keysPressed.w && !keysPressed.a && !keysPressed.s && !keysPressed.d && 
          !keysPressed[' '] && !keysPressed.c) return;
      
      if (keysPressed.w) movementVector.add(horizontalDirection);
      if (keysPressed.s) movementVector.sub(horizontalDirection);
      if (keysPressed.a) movementVector.add(rightVector);
      if (keysPressed.d) movementVector.sub(rightVector);
      
      // Space to move up, C to move down
      if (keysPressed[' ']) movementVector.y += 1;
      if (keysPressed.c) movementVector.y -= 1;
      
      // Normalize movement vector to maintain consistent speed when moving diagonally
      if (movementVector.length() > 0) {
        movementVector.normalize().multiplyScalar(effectiveSpeed);
        
        // Apply movement to camera position. Walls are settled once a frame
        // further down the loop, so no pushout is needed here.
        camera.position.add(movementVector);

        // Update controls target - keep looking in same direction
        controlsRef.current.target.set(
          camera.position.x + direction.x,
          camera.position.y + direction.y, 
          camera.position.z + direction.z
        );
      }
    };

    // Initialize Three.js scene
    const init = () => {
      log('Initializing 3D scene');
      
      // Create scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x87CEEB); // Set a clear sky color as fallback
      sceneRef.current = scene;

      // Create renderer with mobile optimizations
      const renderer = new THREE.WebGLRenderer({ 
        antialias: !isMobile, // Disable antialiasing on mobile for performance
        powerPreference: isMobile ? 'low-power' : 'high-performance'
      });
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
      renderer.shadowMap.enabled = true;
      
      // Mobile performance optimizations
      if (isMobile) {
        renderer.shadowMap.type = THREE.PCFShadowMap; // Simpler shadow type
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio
      } else {
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.setPixelRatio(window.devicePixelRatio);
      }
      
      // Make sure we have a container to append to
      if (containerRef.current) {
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;
      } else {
        console.error("Container reference is null when trying to append renderer");
      }

      // Create camera
      const aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      const camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
      // Set initial camera position from state ref
      const initialPosition = cameraStateRef.current.position;
      camera.position.set(
        initialPosition.x,
        initialPosition.y,
        initialPosition.z
      );
      cameraRef.current = camera;

      // Add controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.1;
      // Set target to look forward at same height
      controls.target.set(
        camera.position.x + 1, // Slightly in front
        camera.position.y,     // Same height as camera
        camera.position.z - 10 // Looking into the scene
      );
      controlsRef.current = controls;

      // Hide the renderer initially until everything is loaded
      renderer.domElement.style.opacity = '0';
      renderer.domElement.style.transition = 'opacity 0.5s ease-in-out';

      // Create a beautiful sky background
      skyboxRef.current = createSkyBackground(scene);

      // Create museum architecture
      createMuseumLayout(scene).then(() => {
        if (process.env.NODE_ENV !== 'production') {
          // Console handle for poking the live scene during development.
          window.museumDebug = { scene, camera, controls: controlsRef.current };
        }
        // Small delay to ensure all textures and materials are ready
        setTimeout(() => {
          // Fade in the 3D scene
          renderer.domElement.style.opacity = '1';
          setIsLoading(false);
          log('Museum initialization complete');
        }, 200);
      }).catch(error => {
        renderer.domElement.style.opacity = '1';
        setIsLoading(false);
        log('Museum initialization failed: ' + error.message);
      });

      // Keep track of decorative elements for animation
      const decorativeElements = scene.children.filter(child => 
        child.isGroup && child.children.some(element => element.userData?.animation)
      );
      
      // Animation loop with smooth camera movement
      // Mobile frame rate limiting for better performance
      let lastFrameTime = 0;
      const targetFPS = isMobile ? 30 : 60;
      const frameInterval = 1000 / targetFPS;
      
      const animate = (currentTime) => {
        animationFrameRef.current = requestAnimationFrame(animate);
        
        // Frame rate limiting for mobile
        if (isMobile && currentTime - lastFrameTime < frameInterval) {
          return;
        }
        lastFrameTime = currentTime;
        
        // Update camera position based on keys pressed
        updateCameraMovement();
        
        // Animate decorative elements
        const time = performance.now() * 0.001; // Convert to seconds

        // Streaming museums (the palace) build and dispose rooms around the
        // camera each frame.
        scene.userData.museumTick?.(camera, time);

        // Animate the centerpiece if it exists
        if (centralpieceRef.current) {
          // Rotate the torus knot
          centralpieceRef.current.rotation.x += 0.01;
          centralpieceRef.current.rotation.y += 0.02;
          
          // Scale pulsation
          const scale = 1 + 0.05 * Math.sin(time * 2);
          centralpieceRef.current.scale.set(scale, scale, scale);
          
          // Color pulsation by changing emissive intensity
          if (centralpieceRef.current.material) {
            centralpieceRef.current.material.emissiveIntensity = 0.3 + 0.2 * Math.sin(time * 3);
          }
        }
        
        // Animate the sky if it exists (subtle rotation)
        if (skyboxRef.current) {
          // Very slow rotation for the sky
          skyboxRef.current.rotation.y = time * 0.01;
        }
        
        // Find and animate all decorative elements
        scene.traverse((object) => {
          if (object.userData?.animation) {
            // Apply rotation animation
            if (object.userData.animation.rotationSpeed) {
              object.rotation.x += object.userData.animation.rotationSpeed.x;
              object.rotation.y += object.userData.animation.rotationSpeed.y;
              object.rotation.z += object.userData.animation.rotationSpeed.z;
            }
            
            // Apply floating animation
            if (object.userData.animation.floatSpeed) {
              const floatOffset = Math.sin(time * object.userData.animation.floatSpeed + object.userData.animation.startTime) * object.userData.animation.floatHeight;
              object.position.y = object.userData.originalY + floatOffset;
            } else {
              // Store original Y position if not already stored
              if (object.userData.originalY === undefined) {
                object.userData.originalY = object.position.y;
              }
            }
          }
        });
        
        // Update controls
        controlsRef.current.update();

        // Walking, dragging, pinching and tilting all move the camera; settle
        // it against the walls here, once, after all of them.
        settleAgainstWalls(controlsRef.current, scene.userData.museumCollide);

        // Update camera position state for display (throttled to avoid too many updates)
        if (currentTime % 100 < frameInterval) { // Update every ~100ms
          setCameraPosition({
            x: parseFloat(camera.position.x.toFixed(2)),
            y: parseFloat(camera.position.y.toFixed(2)),
            z: parseFloat(camera.position.z.toFixed(2))
          });
        }
        
        // Render scene
        rendererRef.current.render(scene, camera);
      };
      
      animate();
      
      // Handle window resize
      const handleResize = () => {
        if (!containerRef.current) return;
        
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      };
      
      window.addEventListener('resize', handleResize);
      
      // --- clicking a painting ---------------------------------------------
      // The palace publishes scene.userData.museumPick; the other museum
      // models don't, and there the pointer just does what it always did.
      //
      // Drag-to-look lives on the same button as the click, so a pointerup
      // only counts as picking up a frame if the pointer barely moved and
      // didn't linger — otherwise every look-around would open whatever was
      // under the cursor when you let go.
      const CLICK_SLOP = 6; // px
      const CLICK_MS = 500;
      const HOVER_EVERY = 120; // ms between hover rays
      const ndc = new THREE.Vector2();
      let pointerDown = null;
      let lastHover = 0;

      const toNdc = (event) => {
        const rect = renderer.domElement.getBoundingClientRect();
        ndc.set(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
        return ndc;
      };

      const handlePointerDown = (event) => {
        pointerDown = { x: event.clientX, y: event.clientY, t: performance.now() };
      };

      const handlePointerUp = (event) => {
        const start = pointerDown;
        pointerDown = null;
        if (!start || openSaveRef.current) return;
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_SLOP) return;
        if (performance.now() - start.t > CLICK_MS) return;
        const hit = scene.userData.museumPick?.(toNdc(event));
        if (hit) {
          renderer.domElement.style.cursor = '';
          setOpenSave(hit);
        }
      };

      // Hover only moves the cursor, so it can be lazy about it — but it casts
      // the same ray the click does, or the cursor promises frames you can't
      // actually open.
      const handlePointerMove = (event) => {
        if (event.pointerType === 'touch' || pointerDown || openSaveRef.current) return;
        if (!scene.userData.museumPick) return;
        const now = performance.now();
        if (now - lastHover < HOVER_EVERY) return;
        lastHover = now;
        renderer.domElement.style.cursor = scene.userData.museumPick(toNdc(event))
          ? 'pointer'
          : '';
      };

      renderer.domElement.addEventListener('pointerdown', handlePointerDown);
      renderer.domElement.addEventListener('pointerup', handlePointerUp);
      renderer.domElement.addEventListener('pointermove', handlePointerMove);

      // Add keyboard controls with smooth movement (desktop only)
      const handleKeyDown = (event) => {
        if (openSaveRef.current) return; // the frame is open; keys belong to it
        const key = event.key.toLowerCase();
        if (['w', 'a', 's', 'd', ' ', 'shift', 'c'].includes(key)) {
          setCameraState(prev => ({
            ...prev,
            keysPressed: {
              ...prev.keysPressed,
              [key]: true
            }
          }));
          event.preventDefault();
        }
      };
      
      const handleKeyUp = (event) => {
        if (openSaveRef.current) return;
        const key = event.key.toLowerCase();
        if (['w', 'a', 's', 'd', ' ', 'shift', 'c'].includes(key)) {
          setCameraState(prev => ({
            ...prev,
            keysPressed: {
              ...prev.keysPressed,
              [key]: false
            }
          }));
          event.preventDefault();
        }
      };
      
      // Add event listeners
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      
      // Add touch event listeners for mobile
      if (isMobile && containerRef.current) {
        const container = containerRef.current;
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: false });
      }
      
      log('Scene initialized successfully');

      // Publish cleanup so the effect below can run it — init() is invoked
      // via setTimeout, so returning this closure from init() would only be
      // discarded by the timer and never called.
      initCleanupRef.current = () => {
        log('Cleaning up 3D scene');
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);

        renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
        renderer.domElement.removeEventListener('pointerup', handlePointerUp);
        renderer.domElement.removeEventListener('pointermove', handlePointerMove);


        // Remove touch event listeners
        if (isMobile && containerRef.current) {
          const container = containerRef.current;
          container.removeEventListener('touchstart', handleTouchStart);
          container.removeEventListener('touchmove', handleTouchMove);
          container.removeEventListener('touchend', handleTouchEnd);
        }
        
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        // The palace stops its streaming tick and drops its live chunks
        // before the blanket traverse below sweeps whatever is left.
        scene.userData.palace?.dispose?.();

        scene.traverse((object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }
          
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
        
        renderer.dispose();
        
        if (containerRef.current && renderer.domElement) {
          containerRef.current.removeChild(renderer.domElement);
        }

        initCleanupRef.current = null;
      };
    };

    // Call init immediately, but wrap in a timeout to improve perceived performance
    // Use longer timeout on mobile to allow UI to render first
    const initTimeout = setTimeout(init, isMobile ? 100 : 50);

    // Return cleanup function
    return () => {
      // If init() hasn't fired yet (or never gets to fire), this cancels it
      // and there is no scene/listeners/render-loop to tear down.
      clearTimeout(initTimeout);
      // If init() already ran, invoke the cleanup it published so the render
      // loop, global listeners, and WebGL context are actually torn down.
      if (initCleanupRef.current) {
        initCleanupRef.current();
      }
    };
  }, [filteredTweets]);

  // Create museum architecture using either custom or procedural method
  const createMuseumLayout = async (scene) => {
    const modelConfig = MUSEUM_MODELS[selectedModel];
    log(`Creating ${useCustomModel ? 'custom' : 'procedural'} museum architecture using ${modelConfig.name}`);
    
    // The loadCustomMuseum function will now handle the dynamic limiting based on actual plane count
    // We pass all filtered tweets and let the museum loader determine how many to use
    const limitedTweets = filteredTweets;
    console.log(`Passing ${limitedTweets.length} tweets to museum loader`);

    if (modelConfig.type === 'generated') {
      // The palace builds itself from the archive: no GLB, no plane matching.
      // It repositions the camera to the selected wing and installs
      // scene.userData.museumTick for streaming.
      const result = await createPalaceMuseum(scene, limitedTweets, {
        spawnClusterId: selectedCluster,
        camera: cameraRef.current,
        controls: controlsRef.current,
        renderer: rendererRef.current,
        mobile: isMobile,
        log,
        progress: setLoadingStatus
      });
      scene.userData.customMuseum = result;
      setLoadingStatus('');
      return result;
    }

    if (useCustomModel) {
      try {
        // Load custom museum model with configuration
        const result = await loadCustomMuseum(scene, limitedTweets, modelConfig);
        log(`${modelConfig.name} loaded with ${result.tweetObjects.length} tweet objects`);
        
        // Store reference for potential later interaction
        scene.userData.customMuseum = result;
        
        // Store centerpiece reference for animation
        if (scene.userData.centerpiece) {
          centralpieceRef.current = scene.userData.centerpiece;
          log('Centerpiece reference stored for animation');
        }
        
        return result; // Return the result to indicate completion
      } catch (error) {
        log(`Failed to load ${modelConfig.name}, falling back to procedural`);
        console.error('Custom museum loading failed:', error);
        
        // Fallback to procedural generation
        return createProceduralMuseum(scene, limitedTweets);
      }
    } else {
      // Create procedural museum
      return createProceduralMuseum(scene, limitedTweets);
    }
  };
  
  const createProceduralMuseum = (scene, limitedTweets) => {
    // Add the centerpiece to the central dome - complex torus knot
    addTweetCenterpiece(scene);
    
    // Use selected cluster to filter tweets if available
    const analysisData = {
      selectedCluster: selectedCluster
    };
    
    // Create the museum with proper entrances and hallways (using limited tweet set)
    const { museum, layoutManager } = createMuseumArchitecture(scene, limitedTweets, analysisData);
    
    // Store the layout manager for potential later interaction
    scene.userData.layoutManager = layoutManager;
    
    log('Procedural museum architecture created successfully');
    
    return { museum, layoutManager }; // Return result to indicate completion
  };

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
    // Need to trigger a resize event after state change to update renderer
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      log(fullscreen ? 'Exiting fullscreen view' : 'Entering fullscreen view');
    }, 100);
  };

  // Start the museum experience after selecting a cluster
  const startMuseum = (clusterId) => {
    setIsLoading(true);
    setSelectedCluster(clusterId);
    
    // Use setTimeout to allow the loading screen to appear before filtering
    setTimeout(() => {
      filterTweetsByTopic(clusterId);
      setClusterSelectionOpen(false);
      log(`Starting museum with cluster ${clusterId !== null ? clusterId : 'All'}`);
    }, 10);
  };

  // The generated palace hangs the whole archive whatever you pick, so a topic
  // is a starting point, not a filter — it only decides which wing you spawn in.
  const isGenerated = MUSEUM_MODELS[selectedModel]?.type === 'generated';

  return (
    <div className={`generative-museum ${fullscreen ? 'fullscreen' : ''}`}>
      {clusterSelectionOpen ? (
        <div className="cluster-selection">
          <h2>{isGenerated ? 'The Endless Palace' : 'Select a Topic Cluster to Explore'}</h2>

          {/* Model Selection */}
          <div className="model-selection">
            <div className="model-options">
              {Object.entries(MUSEUM_MODELS).map(([key, config]) => (
                <label key={key} className="model-option">
                  <input 
                    type="radio" 
                    name="museum-model"
                    value={key}
                    checked={selectedModel === key}
                    onChange={(e) => {
                      const newModel = e.target.value;
                      setSelectedModel(newModel);
                      // Update camera position for new model
                      const newModelConfig = MUSEUM_MODELS[newModel];
                      setCameraState({
                        position: newModelConfig.cameraPosition,
                        target: { x: 0, y: newModelConfig.cameraPosition.y, z: 0 },
                        movementSpeed: 0.5,
                        keysPressed: {}
                      });
                    }}
                  />
                  <div className="model-info">
                    <strong>{config.name}</strong>
                    <p>{config.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          {isGenerated ? (
            <div className="palace-entry">
              <p className="palace-blurb">
                Every save is hung. Each topic becomes its own wing, in its own colour,
                and the building grows around you as you walk.
              </p>
              <button className="palace-enter" onClick={() => startMuseum(null)}>
                Enter at the Rotunda
              </button>
              {availableClusters && (
                <div className="wing-shortcuts">
                  <span className="wing-shortcuts-label">or start in a wing</span>
                  <div className="wing-chips">
                    {availableClusters.map(cluster => (
                      <button
                        key={cluster.topic_id}
                        className="wing-chip"
                        onClick={() => startMuseum(cluster.topic_id)}
                      >
                        {cluster.topic_label || `Topic ${cluster.topic_id + 1}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : availableClusters ? (
            <div className="cluster-grid">
              {availableClusters.map(cluster => (
                <div 
                  key={cluster.topic_id} 
                  className="cluster-item"
                  onClick={() => startMuseum(cluster.topic_id)}
                >
                  <h3>{cluster.topic_label || `Topic ${cluster.topic_id + 1}`}</h3>
                  <div className="keywords">
                    {cluster.keywords.map((keyword, idx) => (
                      <span key={idx} className="keyword">{keyword}</span>
                    ))}
                  </div>
                  <button>Explore This Topic</button>
                </div>
              ))}
              
              <div 
                className="cluster-item all-topics"
                onClick={() => startMuseum(null)}
              >
                <h3>All Topics</h3>
                <p>Explore the full collection</p>
                <button>Start Museum</button>
              </div>
            </div>
          ) : (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading Topics...</div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="museum-container" ref={containerRef}>
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <div className="loading-text">Building Museum...</div>
                <div className="loading-status">{loadingStatus}</div>
              </div>
            )}
            
            <button 
              className="fullscreen-button" 
              onClick={toggleFullscreen}
              title={fullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {fullscreen ? "Exit Fullscreen" : "Expand View"}
            </button>
            
            {/* Mobile Controls */}
            {isMobile && (
              <MobileControls 
                onJoystickMove={handleJoystickMove}
                onJoystickEnd={handleJoystickEnd}
                onVerticalMove={handleVerticalMove}
                onSprintToggle={handleSprintToggle}
                disabled={isLoading || !!openSave}
              />
            )}
            
            <button 
              className="back-button" 
              onClick={() => {
                setOpenSave(null);
                // Clean up 3D scene before returning to cluster selection
                if (animationFrameRef.current) {
                  cancelAnimationFrame(animationFrameRef.current);
                  animationFrameRef.current = null;
                }
                
                // Dispose of 3D resources
                if (sceneRef.current) {
                  sceneRef.current.userData.palace?.dispose?.();
                  sceneRef.current.traverse((object) => {
                    if (object.geometry) {
                      object.geometry.dispose();
                    }
                    if (object.material) {
                      if (Array.isArray(object.material)) {
                        object.material.forEach((material) => material.dispose());
                      } else {
                        object.material.dispose();
                      }
                    }
                  });
                  sceneRef.current.clear();
                }
                
                // Dispose of renderer
                if (rendererRef.current) {
                  rendererRef.current.dispose();
                  if (containerRef.current && rendererRef.current.domElement) {
                    containerRef.current.removeChild(rendererRef.current.domElement);
                  }
                  rendererRef.current = null;
                }
                
                // Reset refs
                sceneRef.current = null;
                cameraRef.current = null;
                controlsRef.current = null;
                centralpieceRef.current = null;
                skyboxRef.current = null;
                
                // Return to cluster selection
                setClusterSelectionOpen(true);
                setIsLoading(true);
              }}
              title="Return to Topic Selection"
            >
              ←
            </button>

            {openSave && (
              <PalaceSaveModal
                save={openSave.tweet}
                room={openSave.room}
                onClose={() => setOpenSave(null)}
                onUpdateSave={() => {}}
              />
            )}
          </div>

          <div className={`museum-controls ${isMobile ? 'mobile' : ''}`}>
            <div className="topic-info">
              <h3>Current Topic:</h3>
              {selectedCluster !== null ? (
                <p>
                  {availableClusters && availableClusters[selectedCluster]?.topic_label || `Topic ${selectedCluster + 1}`}: {availableClusters && 
                    availableClusters[selectedCluster]?.keywords.join(", ")}
                </p>
              ) : (
                <p>All Topics</p>
              )}
            </div>
            
            <div className="camera-position">
              <h3>Camera Position:</h3>
              <p>X: {cameraPosition.x} | Y: {cameraPosition.y} | Z: {cameraPosition.z}</p>
              <p>Model: {MUSEUM_MODELS[selectedModel]?.name}</p>
            </div>
            
            <div className="instructions">
              <h3>Controls:</h3>
              {isMobile ? (
                <>
                  <p>Move: Virtual joystick</p>
                  <p>Up/Down: Touch buttons</p>
                  <p>Sprint: Hold sprint button</p>
                  <p>Look: Touch and drag</p>
                  <p>Zoom: Pinch gesture</p>
                  {advancedGestures && <p>Gyro: Tilt device to look</p>}
                </>
              ) : (
                <>
                  <p>Move: W, A, S, D</p>
                  <p>Up/Down: Space / C</p>
                  <p>Sprint: Hold Shift</p>
                  <p>Look: Click and drag</p>
                  <p>Zoom: Scroll wheel</p>
                </>
              )}
            </div>
            
            {/* Advanced Gestures Toggle for Mobile */}
            {isMobile && (
              <div className="mobile-settings">
                <h3>Mobile Settings:</h3>
                <label className="gesture-toggle">
                  <input 
                    type="checkbox" 
                    checked={advancedGestures}
                    onChange={(e) => setAdvancedGestures(e.target.checked)}
                  />
                  <span>Advanced Gestures</span>
                </label>
                <p className="setting-help">
                  Enables gyroscope camera control
                </p>
              </div>
            )}

            <div className="log-container">
              <h3>Museum Log:</h3>
              <div className="log-messages">
                {logMessages.slice(-5).map((log) => (
                  <div key={log.id} className="log-message">
                    {log.message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GenerativeMuseum;